import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { AppSettings } from '../app.settings';
import { CommonBindingDataService } from '../services/common-binding-data.service';
import { ImagePreviewService } from '../services/image-preview.service';

const DEFAULT_IMAGE_PATHS = [
  '/assets/images/temp/default_profile.png',
  '/assets/images/temp/default_vehicle.svg',
  '/assets/images/temp/default-event.png',
];

@Directive({
  selector: '[appImagePreview]'
})
export class ImagePreviewDirective implements AfterViewInit, OnChanges, OnDestroy {

  @Input() appImagePreviewFileId: string;
  @Input() appImagePreviewFileName: string;

  private img: HTMLImageElement;
  private wrapper: HTMLElement;
  private overlay: HTMLElement;
  private isFallback = false;
  private listeners: (() => void)[] = [];

  constructor(
    private el: ElementRef<HTMLImageElement>,
    private renderer: Renderer2,
    private imagePreviewService: ImagePreviewService,
    private commonBindingDataService: CommonBindingDataService
  ) {
    this.img = this.el.nativeElement;
  }

  ngAfterViewInit() {
    this.buildOverlay();
    this.listeners.push(
      this.renderer.listen(this.img, 'error', () => this.setFallback(true)),
      this.renderer.listen(this.img, 'load', () => this.setFallback(this.isDefaultImage()))
    );
    if (this.img.complete && this.isDefaultImage()) {
      this.setFallback(true);
    }
    this.updateOverlayVisibility();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['appImagePreviewFileId'] && this.overlay) {
      if (this.appImagePreviewFileId) {
        this.setFallback(this.isDefaultImage());
      } else {
        this.setFallback(true);
      }
    }
  }

  ngOnDestroy() {
    this.listeners.forEach(unlisten => unlisten());
  }

  private buildOverlay() {
    const parent = this.renderer.parentNode(this.img);
    this.wrapper = this.renderer.createElement('div');
    this.renderer.addClass(this.wrapper, 'image-preview-wrapper');
    this.renderer.insertBefore(parent, this.wrapper, this.img);
    this.renderer.appendChild(this.wrapper, this.img);

    this.overlay = this.renderer.createElement('div');
    this.renderer.addClass(this.overlay, 'image-preview-overlay');

    const previewBtn = this.createIconButton('pi pi-eye', 'Preview');
    this.listeners.push(
      this.renderer.listen(previewBtn, 'click', event => {
        event.stopPropagation();
        this.openPreview();
      })
    );

    const downloadBtn = this.createIconButton('pi pi-download', 'Download');
    this.listeners.push(
      this.renderer.listen(downloadBtn, 'click', event => {
        event.stopPropagation();
        this.download();
      })
    );

    this.renderer.appendChild(this.overlay, downloadBtn);
    this.renderer.appendChild(this.overlay, previewBtn);
    this.renderer.appendChild(this.wrapper, this.overlay);
  }

  private openPreview() {
    const fileId = this.appImagePreviewFileId;
    const previewUrl = this.getFullPreviewUrl();

    this.imagePreviewService.open(previewUrl, {
      fileId: this.isDownloadableFileId(fileId) ? fileId : '',
      fileName: this.appImagePreviewFileName || this.resolveFileName(previewUrl)
    });
  }

  private getFullPreviewUrl(): string {
    const currentSrc = this.img.currentSrc || this.img.src || '';
    if (currentSrc.indexOf('blob:') === 0) {
      return currentSrc;
    }

    const fileId = (this.appImagePreviewFileId || '').toString().trim();
    if (!fileId) {
      return currentSrc;
    }
    if (/^https?:\/\//i.test(fileId) || fileId.indexOf('/assets/') === 0) {
      return fileId;
    }
    if (fileId.indexOf(AppSettings.GET_FILE_URL) === 0 || fileId.indexOf('/file/') !== -1) {
      return fileId;
    }
    return `${AppSettings.GET_FILE_URL}/${fileId}`;
  }

  private isDownloadableFileId(fileId: string): boolean {
    const id = (fileId || '').toString().trim();
    return !!id
      && !/^https?:\/\//i.test(id)
      && id.indexOf('/assets/') !== 0
      && id.indexOf('/file/') === -1
      && id.indexOf('blob:') !== 0;
  }

  private resolveFileName(url: string): string {
    if (!url || url.indexOf('blob:') === 0) {
      return '';
    }
    try {
      const path = url.split('?')[0];
      const segment = path.substring(path.lastIndexOf('/') + 1);
      const decoded = decodeURIComponent(segment || '');
      return decoded.indexOf('.') > 0 ? decoded : '';
    } catch {
      return '';
    }
  }

  private createIconButton(iconClass: string, title: string): HTMLElement {
    const button = this.renderer.createElement('span');
    this.renderer.addClass(button, 'image-preview-icon');
    this.renderer.setAttribute(button, 'title', title);
    const icon = this.renderer.createElement('i');
    iconClass.split(' ').forEach(cls => this.renderer.addClass(icon, cls));
    this.renderer.appendChild(button, icon);
    return button;
  }

  private download() {
    const currentSrc = this.img.currentSrc || this.img.src || '';
    // Local drop preview is not yet a server file — avoid downloading a stale id.
    if (currentSrc.indexOf('blob:') === 0) {
      return;
    }
    const fileId = (this.appImagePreviewFileId || '').toString().trim();
    if (!this.isDownloadableFileId(fileId)) {
      return;
    }
    this.commonBindingDataService.getDocumentDownload(fileId);
  }

  private isDefaultImage(): boolean {
    const src = this.img.currentSrc || this.img.src || '';
    return DEFAULT_IMAGE_PATHS.some((path) => src.indexOf(path) !== -1);
  }

  private setFallback(isFallback: boolean) {
    this.isFallback = isFallback;
    this.updateOverlayVisibility();
  }

  private updateOverlayVisibility() {
    const fileId = (this.appImagePreviewFileId || '').toString().trim();
    const show = !!fileId && !this.isFallback;
    this.renderer.setStyle(this.overlay, 'display', show ? 'flex' : 'none');
  }
}
